import { api } from '.';
import { IGiftCardLineItem } from '../gift-card-purchase/types';
import { IOrder, PaymentMethod } from '../payment/types';

export enum CreditType {
  GiftCard = 'GiftCard',
}

export type Credit = {
  type: CreditType;
  paymentToken: string;
  isGlobal?: boolean;
};

export interface IPaymentRequest {
  orderId: string;
  paymentToken?: string;
  chargeCardOnFile: boolean;
  saveCardForFutureBookings: boolean;
  paymentMethod: PaymentMethod;
  cardId?: string;
  memo: string;
  credits?: Credit[];
}

export interface IProcessTipRequest extends IPaymentRequest {
  customerId: string;
  tipAmountInDollars: number;
}

export interface IProcessTipAsGuestRequest {
  orderId: string;
  tipAmountInDollars: number;
  paymentToken?: string;
  memo: string;
}
export interface IOrderTreatment {
  name: string;
  price: number;
}

export interface IOrderAddOn {
  name: string;
  price: number;
}

export interface IOrderTimeSlot {
  starts: string;
  durationInMinutes: number;
  timeZone: string;
}

export interface ICreateOrderRequest {
  businessId: string;
  customerId: string;
  locationId: string;
  customerName: string;
  customerEmail: string;
  addOns: IOrderAddOn[];
  treatments: IOrderTreatment[];
  timeSlot: IOrderTimeSlot;
  timeZone: string;
}

export interface IUpdateOrderBeforeDownPaymentRequest {
  orderId: string;
  cardId: string | null;
  chargeCardOnFile: boolean;
  credits: Credit[] | null;
  businessId: string;
  customerId: string;
  addOns: IOrderAddOn[];
  treatments: IOrderTreatment[];
  timeSlot: IOrderTimeSlot;
}

export interface IInitiateTipRequest {
  orderId: string;
  customerId: string;
  tipAmountInDollars: number;
  memo: string;
}

export interface IUpdateTipRequest extends IInitiateTipRequest {
  cardId: string | null;
  customerId: string;
  chargeCardOnFile: boolean;
  credits: Credit[] | null;
  tipId: string;
}

export interface ICaptureTipRequest extends IInitiateTipRequest {
  credits: Credit[] | null;
  customerId: string;
  tipId: string;
  saveCardForFutureBookings: boolean;
}

export interface IInitialOrderResponse {
  location: string;
  clientSecret: string;
  marketplaceFeeCharge: number;
}

export interface IOrderBeforeProcess {
  orderId: string;
  clientSecret: string;
  marketplaceFeeCharge: number;
}

export interface IInitialPaymentResponse {
  location: string;
  clientSecret: string;
}

export interface IPaymentBeforeProcess {
  transactionId: string;
  clientSecret: string;
}

export interface ICreateGiftCardOrderRequest {
  customerId: string;
  customerName: string;
  customerEmail: string;
  timeZone: string;
  lineItems: IGiftCardLineItem[];
}

export interface IUpdateGiftCardOrderRequest {
  orderId: string;
  cardId: string | null;
  chargeCardOnFile: boolean;
  lineItems: IGiftCardLineItem[];
}

// Define a service using a base URL and expected endpoints
export const orderApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrder: builder.query<IOrder, string>({
      query: (orderId) => {
        if (!orderId) throw new Error('bookingId is required.');
        return {
          url: `/order/${orderId}`,
          method: 'GET',
        };
      },
      providesTags: ['order'],
      transformResponse: (response: IOrder) => {
        return response;
      },
    }),
    getOrders: builder.query<IOrder[], void>({
      query: () => ({
        url: `/order/customer/me`,
        method: 'get',
      }),
      providesTags: ['orders'],
    }),
    getOrderByBookingId: builder.query<IOrder, string>({
      query: (bookingId) => ({
        url: `/order/booking/${bookingId}/simple`,
        method: 'get',
      }),
      providesTags: ['booking-order'],
    }),
    addTip: builder.mutation<void, IProcessTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['orders', 'customer-cards'],
    }),
    addTipAsGuest: builder.mutation<void, IProcessTipAsGuestRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip/guest`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['orders', 'customer-cards'],
    }),
    createOrder: builder.mutation<IOrderBeforeProcess, ICreateOrderRequest>({
      query: (data) => {
        return {
          url: `/order`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    updateOrderBeforeDownPayment: builder.mutation<
      IOrderBeforeProcess,
      IUpdateOrderBeforeDownPaymentRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    initiateTip: builder.mutation<IPaymentBeforeProcess, IInitiateTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    updateTip: builder.mutation<IPaymentBeforeProcess, IUpdateTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2/${data.tipId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    captureTip: builder.mutation<void, ICaptureTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-v2/${data.tipId}`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['order', 'booking-order', 'customer-cards'],
    }),
    initiateTipAsGuest: builder.mutation<
      IPaymentBeforeProcess,
      IInitiateTipRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-as-guest`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    updateTipAsGuest: builder.mutation<
      IPaymentBeforeProcess,
      IUpdateTipRequest
    >({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-as-guest/${data.tipId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialPaymentResponse) => {
        const result: IPaymentBeforeProcess = {
          clientSecret: response.clientSecret,
          transactionId: response.location.split('/')[4],
        };

        return result;
      },
      invalidatesTags: ['order'],
    }),
    captureTipAsGuest: builder.mutation<void, ICaptureTipRequest>({
      query: (data) => {
        return {
          url: `/order/${data.orderId}/tip-as-guest/${data.tipId}`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: ['order', 'booking-order', 'customer-cards'],
    }),
    createGiftCardOrder: builder.mutation<
      IOrderBeforeProcess,
      ICreateGiftCardOrderRequest
    >({
      query: (data) => {
        return {
          url: `/gift-card-order`,
          method: 'POST',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    updateGiftCardOrder: builder.mutation<
      IOrderBeforeProcess,
      IUpdateGiftCardOrderRequest
    >({
      query: ({ orderId, ...data }) => {
        return {
          url: `/gift-card-order/${orderId}`,
          method: 'PUT',
          data,
        };
      },
      transformResponse: (response: IInitialOrderResponse) => {
        const result: IOrderBeforeProcess = {
          clientSecret: response.clientSecret,
          marketplaceFeeCharge: response.marketplaceFeeCharge,
          orderId: response.location.split('/')[2],
        };

        return result;
      },
    }),
    completeGiftCardOrder: builder.mutation<void, string>({
      query: (orderId) => {
        return {
          url: `/gift-card-order/${orderId}`,
          method: 'POST',
          data: {
            SaveCard: false,
          },
        };
      },
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useGetOrderByBookingIdQuery,
  useAddTipMutation,
  useAddTipAsGuestMutation,
  useCreateOrderMutation,
  useUpdateOrderBeforeDownPaymentMutation,
  useGetOrderQuery,
  useInitiateTipMutation,
  useCaptureTipMutation,
  useUpdateTipMutation,
  useInitiateTipAsGuestMutation,
  useUpdateTipAsGuestMutation,
  useCaptureTipAsGuestMutation,
  useCreateGiftCardOrderMutation,
  useUpdateGiftCardOrderMutation,
  useCompleteGiftCardOrderMutation,
} = orderApi;
